/**
 * Phase 9.7 v2 — BLUF auto-draft from pinned items.
 *
 * Examines the pinned-items queue and generates a draft BLUF (Bottom Line
 * Up Front) paragraph for the §7.25 Minister Briefing Pack front page.
 * Operator-as-editor pattern: the draft is suggested, never auto-published;
 * the FrontPage shows it ABOVE the editable BLUF prose with a "use this
 * draft" affordance.
 *
 * Per CD R10 Q2 verdict ("PHIA picker frictionless-but-visible"): every
 * generated assertion carries a PHIA likelihood + analytical-confidence
 * computed from the underlying signal strength.
 *
 * The generator is HEURISTIC, not LLM-based. Heuristic gives:
 *   - Deterministic output for the same pins (auditable)
 *   - No network dependency / no token spend
 *   - Honest "this is a stub the operator must polish" framing
 *
 * Phase 11+ swaps the heuristic for a grounded-LLM draft (every sentence
 * cites a specific pin's source) — but the contract stays the same.
 *
 * ── CD R11 Finding #3 expansion (2026-06-01) ─────────────────────────
 * Pre-R10 covered 6 surfaces (§7.4 / §7.7 / §7.12 / §7.24 / §7.26 /
 * §7.27). Phase 10+ shipped 9 more pin-emitting surfaces that all
 * silently fell back to the generic "operator must polish" placeholder.
 * This expansion adds a SHORT, citing pin-contract clause per surface so
 * the BLUF carries surface-specific framing for every pin a Phase 11+
 * operator can drop in. Pattern per surface case:
 *   - 1 sentence reading "Per §7.x [surface name]: …" plus the pinned
 *     datum + a PHIA tag rooted in that datum's signal strength
 *   - Existing scoreToPhia()/bandToConfidence() helpers reused; no LLM,
 *     no randomness — same pins → same prose
 *
 * Surfaces now covered (13/13 pin-emitting surface sections — all four
 * CD R11 Batch-2 differentiators land):
 *   §7.4   Scenario Simulator           (pre-R10)
 *   §7.7   Variant Tracker              (pre-R10)
 *   §7.12  Wastewater Surveillance      (pre-R10)
 *   §7.24  Pandemic Potential Index     (pre-R10)
 *   §7.26  PHEIC Predictor              (pre-R10)
 *   §7.27  Mass-Gathering Calendar      (pre-R10)
 *   §7.28  AAR Library (now in §7.21)   (NEW — historical analogue framing)
 *   §7.29  AMR Feedback Loop            (NEW — stewardship lever framing)
 *   §7.30  Air-Quality Overlay          (NEW — compounded mortality framing)
 *   §7.31  Lab-Bottleneck Forecaster    (NEW — saturation-day framing)
 *   §7.33  Travel-Restriction Sim       (NEW — days-bought framing)
 *   §7.34  Pandemic-Insurance Triggers  (NEW — payout-gap framing)
 *   §7.35  Pre-positioned MCM Inventory (NEW — stockpile-gap framing)
 *
 * Surfaces with no pin affordance (intentional — these are query /
 * navigation / audit destinations rather than briefable signals):
 *   §7.1 / §7.2 / §7.3 / §7.5 / §7.6 / §7.8 / §7.9 / §7.10 / §7.11
 *   §7.13 / §7.14 / §7.15 / §7.16 / §7.17 / §7.18 / §7.19 / §7.20
 *   §7.21 / §7.22 / §7.23 / §7.25 / §7.32 / §7.4b
 * If any of these get a pin affordance in the future, add a matching
 * case here so the BLUF doesn't silently fall back to generic.
 */

import type { PinnedItem } from './pin-to-brief';
import type { PhiaAssertion, PhiaWord } from './types';

export interface BlufDraftClause {
  /** Source surface section — for the "drawn from" footer. */
  surface: string;
  /** Pin id this clause was derived from. */
  pinId: string;
  /** Prose with embedded PHIA-tag placeholders {phia0} / {phia1} / ... */
  prose: string;
  /** PHIA assertions referenced by the prose placeholders. */
  phia: readonly PhiaAssertion[];
}

export interface BlufDraft {
  clauses: readonly BlufDraftClause[];
  /** Combined prose with all PHIA placeholders renumbered globally. */
  combinedProse: string;
  /** All PHIA assertions in placeholder order. */
  combinedPhia: readonly PhiaAssertion[];
  /** Brief "drawn from" footer text. */
  drawnFrom: string;
}

const EMPTY_DRAFT: BlufDraft = {
  clauses: [],
  combinedProse: '',
  combinedPhia: [],
  drawnFrom: '',
};

/** Score 0-100 → PHIA word. */
function scoreToPhia(score: number): PhiaWord {
  if (score >= 90) return 'almost certain';
  if (score >= 75) return 'highly likely';
  if (score >= 55) return 'likely';
  if (score >= 25) return 'realistic possibility';
  if (score >= 15) return 'improbable';
  return 'remote';
}

/** ±uncertainty band → analytical confidence. */
function bandToConfidence(band: number | undefined): 'Low' | 'Moderate' | 'High' {
  if (band == null) return 'Moderate';
  if (band >= 20) return 'Low';
  if (band >= 10) return 'Moderate';
  return 'High';
}

/** Format a number compactly: 3140 → "3.1k", 18000 → "18k", 1_400_000 → "1.4M". */
function abbr(n: number): string {
  if (n >= 1e6) return (n / 1e6).toFixed(1) + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(n >= 1e4 ? 0 : 1) + 'k';
  return String(Math.round(n));
}

/** Safe number coercion — returns undefined if the field is missing or non-numeric. */
function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** Safe string coercion — returns undefined for missing/empty strings. */
function str(v: unknown): string | undefined {
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/** Generate a single clause from one pinned item. Returns null for pins
 *  we don't know how to draft from (operator should add manually). */
function clauseFromPin(pin: PinnedItem): BlufDraftClause | null {
  const ctx = pin.context ?? {};
  switch (pin.surface) {
    case '§7.24': {  // PPI
      const score = typeof pin.value === 'number' ? pin.value : Number(pin.value ?? 0);
      const band = typeof ctx['uncertaintyBand'] === 'number' ? ctx['uncertaintyBand'] : undefined;
      const tier = typeof ctx['tier'] === 'string' ? ctx['tier'] : 'WATCH';
      const phia: PhiaAssertion = {
        word: scoreToPhia(score),
        confidence: bandToConfidence(band),
      };
      return {
        surface: '§7.24',
        pinId: pin.id,
        prose: `The current Pandemic Potential Index sits at ${score}±${band ?? '?'} (${tier}), placing escalation as {phia0} within the planning horizon.`,
        phia: [phia],
      };
    }
    case '§7.26': {  // PHEIC predictor
      const score = typeof pin.value === 'number' ? pin.value : Number(pin.value ?? 0);
      const band = typeof ctx['band'] === 'number' ? ctx['band'] : undefined;
      const estDays = typeof ctx['estDays'] === 'number' ? ctx['estDays'] : undefined;
      const phia: PhiaAssertion = {
        word: scoreToPhia(score),
        confidence: bandToConfidence(band),
      };
      return {
        surface: '§7.26',
        pinId: pin.id,
        prose: estDays != null
          ? `A WHO PHEIC declaration is {phia0} from current trajectory (composite ${score}±${band ?? '?'}; estimated ~${estDays} days to declaration based on historical anchor).`
          : `A WHO PHEIC declaration is {phia0} from current trajectory (composite ${score}±${band ?? '?'}).`,
        phia: [phia],
      };
    }
    case '§7.4': {  // Counterfactual scenario
      const finalDeaths = typeof ctx['finalDeaths'] === 'number' ? ctx['finalDeaths'] : 0;
      const baselineDeaths = typeof ctx['baselineDeaths'] === 'number' ? ctx['baselineDeaths'] : 0;
      const averted = baselineDeaths - finalDeaths;
      const activeNpis = Array.isArray(ctx['activeNpis']) ? ctx['activeNpis'] as string[] : [];
      const npiClause = activeNpis.length > 0
        ? `applying ${activeNpis.join(' + ')} from day ${ctx['decisionDay'] ?? 14}`
        : 'taking no additional measures';
      const phia: PhiaAssertion = {
        word: averted > 0 ? 'likely' : 'realistic possibility',
        confidence: 'Moderate',
      };
      return {
        surface: '§7.4',
        pinId: pin.id,
        prose: averted > 0
          ? `Scenario modelling: ${npiClause} would {phia0} avert ~${abbr(averted)} deaths over the simulated horizon (vs ${abbr(baselineDeaths)} baseline).`
          : `Scenario modelling: ${npiClause} projects ${abbr(finalDeaths)} deaths over the simulated horizon (baseline).`,
        phia: [phia],
      };
    }
    case '§7.7': {  // Variant tracker
      const snv = typeof ctx['snv'] === 'number' ? ctx['snv'] : 0;
      const ins = typeof ctx['ins'] === 'number' ? ctx['ins'] : 0;
      const del = typeof ctx['del'] === 'number' ? ctx['del'] : 0;
      const accession = typeof ctx['accession'] === 'string' ? ctx['accession'] : 'reference';
      const total = snv + ins + del;
      const phia: PhiaAssertion = {
        word: total >= 20 ? 'likely' : 'realistic possibility',
        confidence: 'Low',
      };
      return {
        surface: '§7.7',
        pinId: pin.id,
        prose: `Genomic divergence vs ${accession}: ${snv} SNVs · ${ins} insertions · ${del} deletions. Antigenic or vaccine-escape implications are {phia0} pending sequence-level analysis.`,
        phia: [phia],
      };
    }
    case '§7.12': {  // Wastewater
      const peak = typeof ctx['peakRna'] === 'number' ? ctx['peakRna'] : 0;
      const pop = typeof ctx['catchmentPop'] === 'number' ? ctx['catchmentPop'] : 0;
      const pathogen = typeof ctx['pathogen'] === 'string' ? ctx['pathogen'] : 'pathogen';
      const phia: PhiaAssertion = { word: 'realistic possibility', confidence: 'Moderate' };
      return {
        surface: '§7.12',
        pinId: pin.id,
        prose: `Wastewater signal for ${pathogen}: peak ${peak.toExponential(1)} RNA copies/L in ${pop.toLocaleString()}-population catchment. Community transmission elevation is {phia0} ahead of the clinical-case curve.`,
        phia: [phia],
      };
    }
    case '§7.27': {  // Mass-gathering
      const phase = typeof ctx['phase'] === 'string' ? ctx['phase'] : 'monitor';
      const iso3 = typeof ctx['iso3'] === 'string' ? ctx['iso3'] : '';
      const nextDate = typeof ctx['nextDate'] === 'string' ? ctx['nextDate'] : '';
      const phia: PhiaAssertion = {
        word: phase === 'in-progress' || phase === 't-14d' ? 'likely' : 'realistic possibility',
        confidence: 'Moderate',
      };
      return {
        surface: '§7.27',
        pinId: pin.id,
        prose: `Mass-gathering risk: ${pin.label.replace(/ ·.*$/, '')}${iso3 ? ` (${iso3})` : ''} at phase ${phase}${nextDate ? `, event date ${nextDate}` : ''}. Pathogen-class amplification is {phia0} per the cultural-risk register.`,
        phia: [phia],
      };
    }

    // ── CD R11 Finding #3 expansion ───────────────────────────────────

    case '§7.28': {  // AAR Library — historical analogue framing (now folded into §7.21)
      const outbreak = str(ctx['outbreak']) ?? 'a past outbreak';
      const pathogen = str(ctx['pathogen']) ?? 'an analogous pathogen';
      const years = str(ctx['years']) ?? '';
      const topLesson = str(ctx['topLesson']);
      // Historical analogue — likelihood is about applicability to current
      // scenario, not the historical fact itself. Default-likely with
      // Moderate confidence (operator chose to pin it).
      const phia: PhiaAssertion = { word: 'likely', confidence: 'Moderate' };
      return {
        surface: '§7.28',
        pinId: pin.id,
        prose: topLesson
          ? `Per §7.28 AAR Library: the ${outbreak}${years ? ` (${years})` : ''} ${pathogen} response carries a {phia0}-applicable lesson — "${topLesson.slice(0, 140)}".`
          : `Per §7.28 AAR Library: the ${outbreak}${years ? ` (${years})` : ''} ${pathogen} response is {phia0} the closest historical analogue for the current trajectory.`,
        phia: [phia],
      };
    }

    case '§7.29': {  // AMR feedback loop — stewardship-lever framing
      const projectedPct = num(pin.value) ?? 0;
      const horizonYears = num(ctx['horizonYears']) ?? 5;
      const baselineDdd = num(ctx['baselineDdd']);
      const driver = str(ctx['selectionDriver']) ?? 'antibiotic consumption';
      const consumptionDriven = ctx['consumptionDriven'] === true;
      const scenarioId = str(ctx['scenarioId']) ?? 'status-quo';
      // Confidence is Low for transmission-driven organisms (lever weak),
      // Moderate-to-High for consumption-driven ones.
      const phia: PhiaAssertion = {
        word: projectedPct >= 40 ? 'highly likely' : projectedPct >= 20 ? 'likely' : 'realistic possibility',
        confidence: consumptionDriven ? 'Moderate' : 'Low',
      };
      const leverNote = consumptionDriven
        ? 'stewardship lever is meaningful here'
        : 'stewardship lever is weak — transmission-driven organism';
      return {
        surface: '§7.29',
        pinId: pin.id,
        prose: `Per §7.29 AMR Feedback Loop: ${pin.label.split(' · ')[0]} reaches ${projectedPct.toFixed(1)}% resistance at ${horizonYears}y under ${scenarioId}${baselineDdd != null ? ` (baseline ${baselineDdd} DDD/1000/d)` : ''} — {phia0}, driven by ${driver}; ${leverNote}.`,
        phia: [phia],
      };
    }

    case '§7.30': {  // Air-quality overlay — compounded mortality framing
      const attributableDeaths = num(pin.value) ?? 0;
      const baselineDeaths = num(ctx['baselineDeaths']) ?? 0;
      const multiplier = num(ctx['multiplier']) ?? 1;
      const pm25 = num(ctx['pm25']);
      const pathogenId = str(ctx['pathogenId']) ?? 'pathogen';
      const regionId = str(ctx['regionId']) ?? 'region';
      // Confidence anchored by the literature elasticity (Wu 2020) — Moderate.
      const phia: PhiaAssertion = {
        word: attributableDeaths >= baselineDeaths * 0.2 ? 'likely' : 'realistic possibility',
        confidence: 'Moderate',
      };
      const pmClause = pm25 != null ? ` (PM2.5 ${pm25.toFixed(1)} μg/m³, ×${multiplier.toFixed(2)} mortality multiplier)` : '';
      return {
        surface: '§7.30',
        pinId: pin.id,
        prose: `Per §7.30 Air-Quality Overlay: ${pathogenId} × ${regionId}${pmClause} compounds to +${abbr(attributableDeaths)} air-quality-attributable deaths vs ${abbr(baselineDeaths)} baseline; pre-positioning oxygen + escalated triage is {phia0} warranted (Wu 2020 elasticity anchor).`,
        phia: [phia],
      };
    }

    case '§7.31': {  // Lab-bottleneck forecaster — saturation-day framing
      const sat100 = num(pin.value);
      const reached = sat100 != null && sat100 >= 0;
      const peakDemand = num(ctx['peakDemandPerDay']);
      const baselineCap = num(ctx['baselineDailyCapacity']);
      const testsPerCase = num(ctx['testsPerCase']);
      const withNpi = ctx['withNpiStack'] === true;
      // High confidence when saturation reached within the horizon, Moderate when not.
      const phia: PhiaAssertion = {
        word: reached ? 'highly likely' : 'realistic possibility',
        confidence: reached ? 'High' : 'Moderate',
      };
      const satClause = reached
        ? `lab capacity saturates on day ${sat100}`
        : 'lab capacity does NOT saturate within the simulated horizon';
      const demandClause = peakDemand != null && baselineCap != null
        ? ` (peak demand ${abbr(peakDemand)}/d vs ${abbr(baselineCap)}/d baseline${testsPerCase != null ? ` @ ${testsPerCase} tests/case` : ''})`
        : '';
      return {
        surface: '§7.31',
        pinId: pin.id,
        prose: `Per §7.31 Lab-Bottleneck Forecaster: ${satClause}${demandClause}${withNpi ? ' with NPI mitigation' : ''}; reported case counts {phia0} become unreliable after that day — surge workarounds (pool-test 4× / antigen-shift 5× / contracted-surge 1.75×) detailed in the pinned context.`,
        phia: [phia],
      };
    }

    case '§7.33': {  // Travel-restriction simulator — days-bought framing
      const daysBought = num(pin.value) ?? 0;
      const pctPrevented = num(ctx['pctPreventedAtHorizon']) ?? 0;
      const tierLabel = str(ctx['tierLabel']) ?? 'restriction tier';
      const gdpLow = num(ctx['gdpLowPct']);
      const gdpHigh = num(ctx['gdpHighPct']);
      const horizonDays = num(ctx['horizonDays']);
      // High likelihood for days-bought (geometric); the prevention claim
      // is intentionally framed as honesty-rail (Chinazzi/Mateus anchors).
      const phia: PhiaAssertion = {
        word: daysBought >= 7 ? 'likely' : 'realistic possibility',
        confidence: 'High',
      };
      const gdpClause = gdpLow != null && gdpHigh != null
        ? ` at ${gdpLow.toFixed(1)}-${gdpHigh.toFixed(1)}% GDP cost`
        : '';
      return {
        surface: '§7.33',
        pinId: pin.id,
        prose: `Per §7.33 Travel-Restriction Simulator: ${tierLabel} buys ~${daysBought}d of delay and prevents ${pctPrevented.toFixed(1)}% of imports at d${horizonDays ?? '?'}${gdpClause} — restrictions DELAY, rarely prevent (Chinazzi 2020 / Mateus 2014); a {phia0} window of preparation, not a substitute for domestic readiness.`,
        phia: [phia],
      };
    }

    case '§7.34': {  // Pandemic-insurance triggers — payout-gap framing
      const triggerDay = num(pin.value);
      const pheicDay = num(ctx['pheicDeclarationDay']);
      const spreadClass = str(ctx['spreadClass']) ?? 'observed spread';
      const instruments = Array.isArray(ctx['instruments']) ? ctx['instruments'] as Array<Record<string, unknown>> : [];
      const firing = instruments.filter(i => i['triggerFireDay'] !== null && i['regionEligible'] === true);
      const earliestPayout = firing
        .map(i => num(i['payoutDay']))
        .filter((d): d is number => d != null)
        .sort((a, b) => a - b)[0];
      // PEF failure mode: payouts arrive WEEKS after PHEIC. The clause is
      // intentionally pessimistic-by-evidence (highly likely the gap exists).
      const phia: PhiaAssertion = {
        word: firing.length === 0 ? 'almost certain' : 'highly likely',
        confidence: 'High',
      };
      const firedClause = firing.length === 0
        ? `NONE of the ${instruments.length} reference parametric instruments fire under ${spreadClass}`
        : `${firing.length}/${instruments.length} reference instruments fire (earliest trigger day ${triggerDay ?? '?'}${earliestPayout != null ? `, payout day ${earliestPayout}` : ''})`;
      const gapClause = earliestPayout != null && pheicDay != null
        ? `; ${earliestPayout - pheicDay}-day gap from PHEIC to first payout`
        : '';
      return {
        surface: '§7.34',
        pinId: pin.id,
        prose: `Per §7.34 Pandemic-Insurance Triggers: ${firedClause}${gapClause} — parametric insurance is {phia0} to arrive AFTER the surge window (PEF-2020 anchor: 9wk post-PHEIC); pre-position unconditional fiscal capacity, do not substitute insurance for surge.`,
        phia: [phia],
      };
    }

    case '§7.35': {  // MCM inventory — stockpile-gap framing
      const stockpileCount = num(ctx['stockpileCount']) ?? 0;
      const sevenDayCount = num(ctx['sevenDayDeployableStockpiles']) ?? 0;
      const sevenDayDose = num(ctx['sevenDayDeployableDoseLike']) ?? 0;
      const hasGap = ctx['hasGap'] === true;
      const gapReason = str(ctx['gapReason']);
      const pathogenLabel = str(ctx['pathogenLabel']) ?? 'this pathogen';
      // Gaps are factual (High confidence); applicability of stockpiles
      // to operational reality is Moderate (depends on request mechanism).
      const phia: PhiaAssertion = {
        word: hasGap ? 'almost certain' : sevenDayCount >= 2 ? 'highly likely' : 'likely',
        confidence: hasGap ? 'High' : 'Moderate',
      };
      const body = hasGap
        ? `NO pre-positioned MCM stockpile exists for ${pathogenLabel} — ${gapReason ? gapReason.slice(0, 120) : 'gap documented in the catalog'}; first-generation-interval pharmaceutical response is {phia0} unavailable`
        : `${stockpileCount} stockpile(s) registered for ${pathogenLabel}; ${sevenDayCount} deployable within 7d (~${abbr(sevenDayDose)} dose-like units) — first-generation-interval response is {phia0} feasible via documented request mechanisms`;
      return {
        surface: '§7.35',
        pinId: pin.id,
        prose: `Per §7.35 MCM Inventory: ${body}.`,
        phia: [phia],
      };
    }

    default:
      return null;
  }
}

/** Generate a BLUF draft from a set of pinned items. Returns an empty
 *  draft when no pins yield a draftable clause. Operator-suppressed pins
 *  (Phase 9.7 Q10) are skipped — kept in the queue but excluded from the
 *  rendered draft. */
export function generateBlufDraft(pinned: readonly PinnedItem[]): BlufDraft {
  const clauses: BlufDraftClause[] = [];
  for (const pin of pinned) {
    if (pin.suppressed) continue;
    const clause = clauseFromPin(pin);
    if (clause) clauses.push(clause);
  }
  if (clauses.length === 0) return EMPTY_DRAFT;

  // Renumber PHIA placeholders globally so the combined prose is valid.
  let proseParts: string[] = [];
  const combinedPhia: PhiaAssertion[] = [];
  for (const clause of clauses) {
    let proseLocal = clause.prose;
    clause.phia.forEach((a, localIdx) => {
      const globalIdx = combinedPhia.length;
      combinedPhia.push(a);
      proseLocal = proseLocal.replace(`{phia${localIdx}}`, `{phia${globalIdx}}`);
    });
    proseParts.push(proseLocal);
  }
  const drawnFromSet = new Set(clauses.map(c => c.surface));
  return {
    clauses,
    combinedProse: proseParts.join(' '),
    combinedPhia,
    drawnFrom: [...drawnFromSet].sort().join(' · '),
  };
}
